import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";

const EMPTY = { prepaid_fee_pct: 2.0, prepaid_flat_fee: 0.0, gst_on_pg_fee: true, cod_rule: "hybrid", cod_flat_fee: 40.0, cod_fee_pct: 2.0 };

export default function FeeSettingsDrawer({ open, onOpenChange }) {
  const { current } = useWorkspace();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !current) return;
    api.get("/fee-settings", { params: { workspace_id: current.id } }).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => setForm(EMPTY));
  }, [open, current]);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/fee-settings", {
        prepaid_fee_pct: Number(form.prepaid_fee_pct),
        prepaid_flat_fee: Number(form.prepaid_flat_fee),
        gst_on_pg_fee: !!form.gst_on_pg_fee,
        cod_rule: form.cod_rule,
        cod_flat_fee: Number(form.cod_flat_fee),
        cod_fee_pct: Number(form.cod_fee_pct),
        marketplace_fees: Object.fromEntries(Object.entries(form.marketplace_fees || {}).map(([k, v]) => [k, Number(v)])),
      }, { params: { workspace_id: current.id } });
      toast.success("Fee settings saved. New CSV uploads will use these.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg rounded-none overflow-y-auto" data-testid="fee-settings-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl flex items-center gap-2"><Settings2 className="h-5 w-5" /> Payment Gateway & COD Fees</SheetTitle>
          <SheetDescription className="text-xs">Applied to new CSV uploads. CSV rows with an explicit Payment Fee column override these defaults.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="border border-border p-4 space-y-4">
            <div className="overline text-muted-foreground">Prepaid orders</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Fee %</Label>
                <Input type="number" step="0.01" value={form.prepaid_fee_pct} onChange={(e) => setForm({ ...form, prepaid_fee_pct: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="fee-prepaid-pct" />
              </div>
              <div>
                <Label className="overline">Flat Fee ₹</Label>
                <Input type="number" step="0.01" value={form.prepaid_flat_fee} onChange={(e) => setForm({ ...form, prepaid_flat_fee: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="fee-prepaid-flat" />
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <Label className="text-sm">Apply 18% GST on PG Fee</Label>
              <Switch checked={form.gst_on_pg_fee} onCheckedChange={(v) => setForm({ ...form, gst_on_pg_fee: v })} data-testid="fee-gst-toggle" />
            </div>
          </div>

          <div className="border border-border p-4 space-y-4">
            <div className="overline text-muted-foreground">COD orders</div>
            <div>
              <Label className="overline">Fee Rule</Label>
              <Select value={form.cod_rule} onValueChange={(v) => setForm({ ...form, cod_rule: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="fee-cod-rule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat ₹ per order</SelectItem>
                  <SelectItem value="percent">Percentage of order</SelectItem>
                  <SelectItem value="hybrid">Hybrid — Max(Flat, Percent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Flat Fee ₹</Label>
                <Input type="number" step="0.01" value={form.cod_flat_fee} onChange={(e) => setForm({ ...form, cod_flat_fee: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="fee-cod-flat" />
              </div>
              <div>
                <Label className="overline">Fee %</Label>
                <Input type="number" step="0.01" value={form.cod_fee_pct} onChange={(e) => setForm({ ...form, cod_fee_pct: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="fee-cod-pct" />
              </div>
            </div>
          </div>
          <div className="border border-border p-4 space-y-4">
            <div className="overline text-muted-foreground">Marketplace fees (%)</div>
            <div className="grid grid-cols-3 gap-3">
              {["shopify", "amazon", "flipkart", "offline", "other"].map((ch) => (
                <div key={ch}>
                  <Label className="overline uppercase">{ch}</Label>
                  <Input
                    type="number" step="0.01"
                    value={(form.marketplace_fees && form.marketplace_fees[ch]) ?? 0}
                    onChange={(e) => setForm({ ...form, marketplace_fees: { ...(form.marketplace_fees || {}), [ch]: e.target.value } })}
                    className="rounded-none mt-1 font-mono"
                    data-testid={`fee-mkt-${ch}`}
                  />
                </div>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground">Applied per-line at order commit and shown in the Order Ledger's Net Profit column.</div>
          </div>
        </div>

        <SheetFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={loading} className="rounded-none" data-testid="fee-save-btn">
            {loading ? "Saving…" : "Save settings"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
